import { createContext, ReactNode, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { DialogListJSONAPI } from '~/api/resources/dialog_list';
import { ListJSONAPI } from '~/api/resources/list';
import { OpportunityJSONAPI } from '~/api/resources/opportunity';
import { Lists } from '~/api/scheme';
import { CreateParams } from '~/api/scheme/lists';
import { OnAddToListSuccessCallback } from '~/components/agents/types';
import { DEFAULT_CREATE_PARAMS } from '~/components/lists/constants';
import { RowsData } from '~/hooks/table/useSelectedRows';

export type ListsDialogsContextProps = {
  addToListOpened: boolean;
  openAddToListDialog: (
    selectedIds: string[],
    successCallback?: OnAddToListSuccessCallback,
    listType?: ListSTI,
    selectedRows?: RowsData[]
  ) => void;
  closeAddToListDialog: () => void;
  selectedListId: string;
  syncType: string;
  selectedIds: string[];
  selectedRows: (RowsData | OpportunityJSONAPI)[];
  createListOpened: boolean;
  createListParams: CreateParams | undefined;
  openCreateListDialog: () => void;
  reviewCreditsOpened: boolean;
  openReviewCreditsDialog: (
    selectedIds: string[],
    selectedRows: (RowsData | OpportunityJSONAPI)[],
    listId: string,
    syncType: string,
    createListParams?: CreateParams
  ) => void;
  closeReviewCreditsDialog: (triggerSyncCompleted: boolean) => void;
  insufficientCreditsOpened: boolean;
  openInsufficientCreditsDialog: (
    selectedIds: string[],
    selectedRows: (RowsData | OpportunityJSONAPI)[]
  ) => void;
  closeInsufficientCreditsDialog: () => void;
  opportunitieSyncComplete: boolean;
  closeCreateListDialog: () => void;
  openLeavePage: () => void;
  editListOpened: boolean;
  openEditListDialog: () => void;
  closeEditListDialog: () => void;
  onClickBack: () => void;
  leavePageOpened: boolean;
  onCancel: () => void;
  onClickLeaveAnyway: () => void;
  lists: {
    OpportunityList: DialogListJSONAPI[];
    RealtorList: DialogListJSONAPI[];
    LoanOfficerList: DialogListJSONAPI[];
  };
  listsRefetch: () => void;
  editableList: ListJSONAPI | null;
  setEditableList: (list: ListJSONAPI) => void;
  setCallback: (callback: (...args: any) => void, type: SuccessCallback) => void;
  creatableListType: ListSTI;
  setCreatableListType: (type: ListSTI) => void;
  onEditListSuccessCallback: ((...args: any) => void) | null;
  onCreateListSuccessCallback: ((listId: string, selectedIds: string[]) => void) | null;
  onAddToListSuccessCallback: OnAddToListSuccessCallback | null;
};

const initialState: ListsDialogsContextProps = {
  addToListOpened: false,
  openAddToListDialog: () => {},
  closeAddToListDialog: () => {},
  selectedListId: '',
  syncType: '',
  selectedIds: [],
  selectedRows: [],
  createListOpened: false,
  createListParams: { attributes: DEFAULT_CREATE_PARAMS, type: 'LoanOfficerList' },
  openCreateListDialog: () => {},
  reviewCreditsOpened: false,
  openReviewCreditsDialog: () => {},
  closeReviewCreditsDialog: () => {},
  insufficientCreditsOpened: false,
  openInsufficientCreditsDialog: () => {},
  closeInsufficientCreditsDialog: () => {},
  opportunitieSyncComplete: false,
  closeCreateListDialog: () => {},
  openLeavePage: () => {},
  editListOpened: false,
  openEditListDialog: () => {},
  closeEditListDialog: () => {},
  onClickBack: () => {},
  leavePageOpened: false,
  onCancel: () => {},
  onClickLeaveAnyway: () => {},
  lists: {
    OpportunityList: [],
    RealtorList: [],
    LoanOfficerList: [],
  },
  listsRefetch: () => {},
  editableList: null,
  setEditableList: () => {},
  creatableListType: 'LoanOfficerList',
  setCreatableListType: () => {},
  onEditListSuccessCallback: null,
  onCreateListSuccessCallback: null,
  onAddToListSuccessCallback: null,
  setCallback: () => {},
};

const ListsDialogsContext = createContext(initialState);

type ListsActionsProviderProps = {
  children: ReactNode;
};

type SuccessCallback = 'add' | 'create' | 'edit';

const ListsDialogsProvider = ({ children }: ListsActionsProviderProps) => {
  const [addToListOpened, setAddToListOpened] = useState(false);
  const [editListOpened, setEditListOpened] = useState(false);
  const [createListOpened, setCreateListOpened] = useState(false);
  const [createListParams, setCreateListParams] = useState<CreateParams | undefined>({
    attributes: DEFAULT_CREATE_PARAMS,
    type: 'LoanOfficerList' as ListSTI,
  });
  const [reviewCreditsOpened, setReviewCreditsOpened] = useState(false);
  const [insufficientCreditsOpened, setInsufficientCreditsOpened] = useState(false);
  const [leavePageOpened, setIsOpenLeavePage] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [syncType, setSyncType] = useState('');
  const [opportunitieSyncComplete, setOpportunitiesSyncComplete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<(RowsData | OpportunityJSONAPI)[]>([]);
  const [creatableListType, setCreatableListType] = useState<ListSTI>('LoanOfficerList');
  const [onEditListSuccessCallback, setEditSuccessCallback] = useState<VoidFunction | null>(null);
  const [onCreateListSuccessCallback, setCreateListSuccessCallback] = useState<VoidFunction | null>(
    null
  );
  const [onAddToListSuccessCallback, setAddToListSuccessCallback] = useState<VoidFunction | null>(
    null
  );

  const setCallback = (callback: VoidFunction, type: SuccessCallback) => {
    if (type === 'add') {
      setAddToListSuccessCallback(() => callback);
    } else if (type === 'create') {
      setCreateListSuccessCallback(() => callback);
    } else if (type === 'edit') {
      setEditSuccessCallback(() => callback);
    }
  };

  const openEditListDialog = () => {
    setEditListOpened(true);
    setCreateListOpened(false);
    setAddToListOpened(false);
  };

  const closeEditListDialog = () => {
    setIsOpenLeavePage(true);
  };

  const openCreateListDialog = () => {
    setCreateListOpened(true);
    setEditListOpened(false);
    setAddToListOpened(false);
  };

  const openReviewCreditsDialog = (
    selectedIds: string[],
    selectedRows: (RowsData | OpportunityJSONAPI)[],
    listId: string,
    syncType: string,
    createListParams?: CreateParams
  ) => {
    setSelectedIds(selectedIds);
    setSelectedRows(selectedRows);
    setSelectedListId(listId);
    setSyncType(syncType);
    setCreateListParams(createListParams);
    setReviewCreditsOpened(true);
    setEditListOpened(false);
    setAddToListOpened(false);
    setCreateListOpened(false);
    setIsOpenLeavePage(false);
  };

  const closeReviewCreditsDialog = (triggerSyncCompleted: boolean) => {
    if (triggerSyncCompleted) {
      setTimeout(() => {
        setOpportunitiesSyncComplete((prev) => !prev);
      }, 300);
    }
    setCreateListOpened(false);
    setReviewCreditsOpened(false);
    setSelectedIds([]);
    setSelectedRows([]);
  };

  const openInsufficientCreditsDialog = (
    selectedIds: string[],
    selectedRows: (RowsData | OpportunityJSONAPI)[]
  ) => {
    setSelectedIds(selectedIds);
    setSelectedRows(selectedRows);
    setInsufficientCreditsOpened(true);
    setCreateListOpened(false);
    setAddToListOpened(false);
  };
  const closeInsufficientCreditsDialog = () => setInsufficientCreditsOpened(false);

  const closeCreateListDialog = () => {
    setCreateListOpened(false);
  };

  const openLeavePage = () => {
    setIsOpenLeavePage(true);
  };

  const openAddToListDialog = (
    selectedIds: string[],
    successCallback: OnAddToListSuccessCallback = () => {},
    listType: ListSTI = 'LoanOfficerList',
    selectedRows: RowsData[] = []
  ) => {
    setAddToListOpened(true);
    setCreatableListType(listType);
    setAddToListSuccessCallback(() => successCallback);
    setSelectedIds(selectedIds);
    setSelectedRows(selectedRows);
  };

  const closeAddToListDialog = () => {
    setAddToListOpened(false);
    setSelectedIds([]);
    setSelectedRows([]);
  };

  const onClickBack = () => {
    setAddToListOpened(false);
    setCreateListOpened(false);
    setEditListOpened(false);
  };

  const onCancel = () => {
    setIsOpenLeavePage(false);
  };

  const onClickLeaveAnyway = () => {
    setCreateListOpened(false);
    setEditListOpened(false);
    setIsOpenLeavePage(false);
    setSelectedIds([]);
    setSelectedRows([]);
  };

  const [editableList, setEditableList] = useState<ListJSONAPI | null>(null);
  const [loanOfficerLists, setLoanOfficerLists] = useState<ListJSONAPI[]>([]);
  const [agentLists, setAgentLists] = useState<ListJSONAPI[]>([]);
  const [opportunityLists, setOpportunityLists] = useState<ListJSONAPI[]>([]);

  const { refetch: listsRefetch } = useQuery('dialogLists', Lists.dialogLists, {
    onSuccess: (listsResponse) => {
      const { loan_officers, realtors, opportunities } = listsResponse.data;

      setLoanOfficerLists(loan_officers.data);
      setAgentLists(realtors.data);
      setOpportunityLists(opportunities.data);
    },
  });

  const lists = useMemo(
    () => ({
      OpportunityList: opportunityLists,
      RealtorList: agentLists,
      LoanOfficerList: loanOfficerLists,
    }),
    [loanOfficerLists, agentLists, opportunityLists]
  );

  return (
    <ListsDialogsContext.Provider
      value={{
        addToListOpened,
        openAddToListDialog,
        closeAddToListDialog,
        selectedListId,
        syncType,
        selectedIds,
        selectedRows,
        createListOpened,
        createListParams,
        openCreateListDialog,
        reviewCreditsOpened,
        openReviewCreditsDialog,
        closeReviewCreditsDialog,
        insufficientCreditsOpened,
        openInsufficientCreditsDialog,
        closeInsufficientCreditsDialog,
        opportunitieSyncComplete,
        closeCreateListDialog,
        openLeavePage,
        editListOpened,
        openEditListDialog,
        closeEditListDialog,
        onClickBack,
        leavePageOpened,
        onCancel,
        onClickLeaveAnyway,
        editableList,
        setEditableList,
        lists,
        listsRefetch,
        creatableListType,
        setCreatableListType,
        onAddToListSuccessCallback,
        onCreateListSuccessCallback,
        onEditListSuccessCallback,
        setCallback,
      }}
    >
      {children}
    </ListsDialogsContext.Provider>
  );
};

export { ListsDialogsProvider, ListsDialogsContext };
